// src/components/pages/dialogs/NewBreakDialog.tsx
//
// Crea un descanso puntual. Se abre desde el icono ☕ de la barra del
// calendario (junto al de actualizar), con la fecha de hoy y las horas en
// blanco por defecto: al no venir de arrastrar una franja, no hay hora de
// partida que precargar.
//
// Mismo patrón de hoja inferior en móvil / modal centrado en escritorio que
// el resto de diálogos (CalendarSettingsDialog, NewAppointmentDialog...):
// clases calcadas para que no desentone.

import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { X, Coffee } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { crearDescanso } from '@/components/hooks/useDescansos';

export interface NewBreakInitialData {
  fecha: string; // YYYY-MM-DD
  hora: string; // HH:MM
  horaFin: string; // HH:MM
}

interface Props {
  open: boolean;
  onClose: () => void;
  onCreado: () => void;
  idUsuario: string;
  idEmpresa: string;
  initialData: NewBreakInitialData;
}

// ── DialogTopBar (mismo estilo que el resto de modales) ──
const DialogTopBar = ({ onClose }: { onClose: () => void }) => (
  <button
    onClick={onClose}
    className="absolute top-2.5 right-3 z-10 shrink-0 w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
    aria-label="Cerrar"
  >
    <X className="w-5 h-5" />
  </button>
);

const NewBreakDialog = ({ open, onClose, onCreado, idUsuario, idEmpresa, initialData }: Props) => {
  const [fecha, setFecha] = useState(initialData.fecha);
  const [horaInicio, setHoraInicio] = useState(initialData.hora);
  const [horaFin, setHoraFin] = useState(initialData.horaFin);
  const [motivo, setMotivo] = useState('');
  const [guardando, setGuardando] = useState(false);

  // Cada apertura parte de la selección que la originó, no de lo que
  // hubiera quedado escrito la vez anterior.
  useEffect(() => {
    if (!open) return;
    setFecha(initialData.fecha);
    setHoraInicio(initialData.hora);
    setHoraFin(initialData.horaFin);
    setMotivo('');
  }, [open, initialData]);

  const horasValidas = !!fecha && !!horaInicio && !!horaFin && horaFin > horaInicio;

  const handleGuardar = async () => {
    if (!horasValidas || guardando) return;
    setGuardando(true);

    const { ok, citasSolapadas } = await crearDescanso({
      id_usuario: idUsuario,
      id_empresa: idEmpresa,
      fecha,
      hora_inicio: horaInicio,
      hora_fin: horaFin,
      motivo,
    });

    setGuardando(false);
    if (!ok) return;

    if (citasSolapadas) {
      toast.warning(
        citasSolapadas === 1
          ? 'Descanso creado. Hay 1 cita en ese horario.'
          : `Descanso creado. Hay ${citasSolapadas} citas en ese horario.`
      );
    } else {
      toast.success('Descanso creado');
    }
    onCreado();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
    >
      <DialogContent
        className="
          z-[60] p-0 border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1c1c1e]
          [&>button:last-child]:hidden
          flex flex-col
          !bottom-0 !left-0 !top-auto !translate-x-0 !translate-y-0
          w-full max-w-none h-auto max-h-[85vh] rounded-t-2xl rounded-b-none
          data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom
          data-[state=open]:duration-300 data-[state=closed]:duration-200
          data-[state=open]:ease-out data-[state=closed]:ease-in
          sm:!bottom-auto sm:!left-1/2 sm:!top-1/2 sm:!-translate-x-1/2 sm:!-translate-y-1/2
          sm:w-[95%] sm:max-w-md sm:mx-auto sm:h-auto sm:max-h-[85vh] sm:rounded-xl sm:shadow-xl
          sm:data-[state=open]:zoom-in-95 sm:data-[state=closed]:zoom-out-95
          sm:data-[state=open]:slide-in-from-bottom-0 sm:data-[state=closed]:slide-out-to-bottom-0
        "
      >
        <DialogTopBar onClose={onClose} />

        <DialogHeader className="px-5 pt-4 pb-1 pr-14 shrink-0">
          <DialogTitle className="flex items-center gap-2 text-sm font-semibold text-[#103254] dark:text-blue-300">
            <Coffee className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            Nuevo descanso
          </DialogTitle>
        </DialogHeader>

        <div className="px-5 py-3 flex-1 overflow-y-auto">
          <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-sm p-4 space-y-4 text-sm text-gray-700 dark:text-gray-300">
            <div>
              <label className="block font-medium mb-1 text-gray-700 dark:text-gray-300">
                Fecha
              </label>
              <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
            </div>

            <div className="flex gap-3">
              <div className="flex-1">
                <label className="block font-medium mb-1 text-gray-700 dark:text-gray-300">
                  Desde
                </label>
                <Input
                  type="time"
                  value={horaInicio}
                  onChange={(e) => setHoraInicio(e.target.value)}
                />
              </div>
              <div className="flex-1">
                <label className="block font-medium mb-1 text-gray-700 dark:text-gray-300">
                  Hasta
                </label>
                <Input type="time" value={horaFin} onChange={(e) => setHoraFin(e.target.value)} />
              </div>
            </div>
            {!horasValidas && fecha && horaInicio && horaFin && (
              <p className="text-xs text-red-500">
                La hora de fin debe ser posterior a la de inicio.
              </p>
            )}

            <div>
              <label className="block font-medium mb-1 text-gray-700 dark:text-gray-300">
                Motivo <span className="text-gray-400 font-normal">(opcional)</span>
              </label>
              <Input
                type="text"
                placeholder="Comida, gestión personal…"
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                maxLength={80}
              />
            </div>
          </div>
        </div>

        <DialogFooter className="mt-2 px-5 pt-2 pb-5 sm:pb-4 flex flex-col sm:flex-row sm:justify-end gap-3 shrink-0 border-t border-gray-100 dark:border-white/10">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={guardando}
            className="w-full sm:w-auto rounded-lg font-medium bg-transparent dark:bg-transparent border-gray-300 dark:border-white/10 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
          >
            Cancelar
          </Button>

          <Button
            onClick={handleGuardar}
            disabled={!horasValidas || guardando}
            className="w-full sm:w-auto rounded-xl text-white font-semibold text-base shadow-md bg-[#103254] hover:bg-[#0d2742] dark:bg-[#103254] dark:hover:bg-[#0d2742] py-3 disabled:opacity-50 transition-colors"
          >
            {guardando ? 'Guardando…' : 'Crear descanso'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NewBreakDialog;